import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// StorageUsage type — defined here so it can be shared between the
// presentational bar (shared-frontend-ui) and the per-app data hooks
// (webclient/src/hooks/useStorageUsage.ts, adminapp/src/hooks/useStorageUsage.ts).
// ---------------------------------------------------------------------------

export interface StorageUsage {
  used_bytes: number;
  limit_bytes: number | null;
  unlimited: boolean;
  percentage: number | null;
  reconciled_at?: string | null;
  /** The non-current media versions' share of used_bytes (pic-service only). */
  versions_bytes?: number;
}

// ---------------------------------------------------------------------------
// Byte formatter — small local helper (no external dep)
// ---------------------------------------------------------------------------

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface StorageUsageBarProps {
  usage: StorageUsage;
}

// ---------------------------------------------------------------------------
// Color logic: ok → is-success, warning (≥80%) → is-warning, danger (≥100%) → is-danger
// ---------------------------------------------------------------------------

function barColorClass(pct: number): string {
  if (pct >= 100) return 'is-danger';
  if (pct >= 80) return 'is-warning';
  return 'is-success';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StorageUsageBar({ usage }: StorageUsageBarProps) {
  const { t } = useTranslation();
  const { used_bytes, limit_bytes, unlimited, percentage } = usage;

  if (unlimited) {
    return (
      <div className="storage-usage-bar">
        <span className="storage-usage-bar__label">
          <span>{formatBytes(used_bytes)}</span>{' '}
          <span className="tag is-info is-light">{t('storage.unlimited', 'unlimited')}</span>
        </span>
      </div>
    );
  }

  // Clamp the displayed bar to 100 even when over-quota
  const pct = percentage ?? 0;
  const clampedPct = Math.min(pct, 100);
  const colorClass = barColorClass(pct);

  const usedLabel = formatBytes(used_bytes);
  const limitLabel = limit_bytes !== null ? formatBytes(limit_bytes) : '?';

  return (
    <div className="storage-usage-bar">
      <progress
        className={`progress ${colorClass} mb-1`}
        role="progressbar"
        value={clampedPct}
        max={100}
        aria-valuenow={clampedPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('storage.usage_aria_label', 'Storage usage')}
      />
      <span className="storage-usage-bar__label is-size-7">
        {usedLabel} / {limitLabel}{' '}
        <span>({Math.round(pct)}%)</span>
      </span>
    </div>
  );
}

export default StorageUsageBar;
